import { createEventHook } from "@vueuse/core";
import { getObjectPermalinkWithoutRouter } from "../permalinks";
import { useDocumentStore } from "../store/document";
import { useBusEmitterListener } from "./index";

export const linkCopied = createEventHook<string>();

export enum EditorModeType {
  Select = 1,
  SliceImage = 2,
  Connector = 3,
  Vector = 4,
  VectorAdd = 5,
  Rectangle = 6,
  StickyNote = 7,
  CardMode = 8,
  Oval = 9,
  Paragraph = 10,
  Line = 11,
  Pencil = 12,
  Shape = 13,
  Pan = 14,
  Comment = 15,
  QuickInsert = 16,
  EditConnectorPoints = 17,
  EditGradientStops = 18,
}

const editorModeChangedEvent = Symbol("bridge:currentEditorModeChanged");
const {
  emitter: currentEditorModeChangedFn,
  listener: onCurrentEditorModeChangedFn,
} = useBusEmitterListener<{ mode: number }>(editorModeChangedEvent);

export function currentEditorModeChanged(mode: number) {
  currentEditorModeChangedFn({ mode });
}
export const onCurrentEditorModeChanged = onCurrentEditorModeChangedFn;

const isMouseOverGradientModeHotZoneEvent = Symbol("bridge:isMouseOverGradientModeHotZoneChanged");
const {
  emitter: isMouseOverGradientModeHotZoneChangedFn,
  listener: onIsMouseOverGradientModeHotZoneChangedFn,
} = useBusEmitterListener<{ isMouseOverGradientModeHotZone: boolean }>(isMouseOverGradientModeHotZoneEvent);

export function isMouseOverGradientModeHotZoneChanged(isMouseOverGradientModeHotZone: boolean) {
  isMouseOverGradientModeHotZoneChangedFn({ isMouseOverGradientModeHotZone });
}
export const onIsMouseOverGradientModeHotZoneChanged = onIsMouseOverGradientModeHotZoneChangedFn;

const shapeManagerPointsUpdatedEvent = Symbol("bridge:shapeManagerPointsUpdated");
export const {
  emitter: shapeManagerPointsUpdated,
  listener: onShapeManagerPointsUpdated,
} = useBusEmitterListener(shapeManagerPointsUpdatedEvent);

const gradientModeGeometryChangedEvent = Symbol("bridge:gradientModeGeometryChanged");
export const {
  emitter: gradientModeGeometryChanged,
  listener: onGradientModeGeometryChanged,
} = useBusEmitterListener(gradientModeGeometryChangedEvent);

const bridgeReadyEvent = Symbol("bridge:bridgeReady");
export const {
  emitter: bridgeReady,
  listener: onBridgeReady,
} = useBusEmitterListener(bridgeReadyEvent);

const scrollZoomChangedEvent = Symbol("bridge:scrollZoomChangedEvent");
const {
  emitter: scrollZoomChangedFn,
  listener: onScrollZoomChangedFn,
} = useBusEmitterListener<{ x: number; y: number; zoom: number; panelX: number; panelY: number; panelWidth: number; panelHeight: number }>(scrollZoomChangedEvent);

export function scrollZoomChanged(x: number, y: number, zoom: number, panelX: number, panelY: number, panelWidth: number, panelHeight: number) {
  scrollZoomChangedFn({ x, y, zoom, panelX, panelY, panelWidth, panelHeight });
}
export const onScrollZoomChanged = onScrollZoomChangedFn;

function getShortcut() {
  const documentStore = useDocumentStore();
  return documentStore.shortcut;
}

export function copyObjectLink(objectId: string) {
  // don't import router and construct url manually
  // this is currently required as a workaround for circular-dependency issue from DiskoHost
  const shortcut = getShortcut();
  if (!shortcut) {
    throw new Error("Tried to create link but current shortcut is unknown.");
  }

  const url = getObjectPermalinkWithoutRouter(shortcut, objectId.replaceAll("-", ""));
  void navigator.clipboard.writeText(url);

  linkCopied.trigger(shortcut);
}
